import { QueryFunctionContext, useInfiniteQuery, useQueryClient } from 'react-query';
import { Options } from '@frontend/fetch';
import {
  LimitedSchemaInfiniteQueryOptions,
  LimitedSchemaFetchInfiniteQueryOptions,
} from '@frontend/react-query-helpers';
import { SchemaAnalyticsGateway1Service } from '../service';
import { GetAppointmentsWaitListIO } from '../types';

export const getGetAppointmentsWaitListQueryKey = (req: GetAppointmentsWaitListIO['input']) =>
  ['AnalyticsGatewayV1', 'GetAppointmentsWaitList', req] as const;
type QueryKey = ReturnType<typeof getGetAppointmentsWaitListQueryKey>;
type QueryFnArgs = QueryFunctionContext<QueryKey, GetAppointmentsWaitListPageParams>;

type GetAppointmentsWaitListPageParams = {
  pageToken?: string;
};
/**
 * A hook that returns an infinite query for the `GetAppointmentsWaitList` endpoint.
 * @param request The request object to pass to the query. Overriding pagination fields on the request is handled by the hook.
 * @param options (optional) The options to pass to `useInfiniteQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetAppointmentsWaitListQuery = <E = unknown, D = GetAppointmentsWaitListIO['output']>(
  req: GetAppointmentsWaitListIO['input'],
  options?: Omit<
    LimitedSchemaInfiniteQueryOptions<GetAppointmentsWaitListIO, E, D, QueryKey>,
    'getNextPageParam' | 'getPreviousPageParam'
  >,
  httpOptions?: Options
) =>
  useInfiniteQuery<GetAppointmentsWaitListIO['output'], E, D, QueryKey>({
    queryKey: getGetAppointmentsWaitListQueryKey(req),
    queryFn: ({ pageParam }: QueryFnArgs) => {
      return SchemaAnalyticsGateway1Service.GetAppointmentsWaitList(
        {
          ...req,
          pageToken: pageParam?.pageToken,
        },
        httpOptions
      );
    },
    ...options,
    getNextPageParam: (lastPage): GetAppointmentsWaitListPageParams | undefined => {
      if (lastPage.nextPageToken) {
        return { pageToken: lastPage.nextPageToken };
      }
      return undefined;
    },
    getPreviousPageParam: undefined,
  });

/**
 * A hook that returns a function to fetch the query for the `GetAppointmentsWaitList` endpoint.
 * It uses `queryClient.fetchInfiniteQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useInfiniteQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchInfiniteQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useFetchGetAppointmentsWaitList = () => {
  const queryClient = useQueryClient();

  const fetchGetAppointmentsWaitList = async <E = unknown, D = GetAppointmentsWaitListIO['output']>(
    req: GetAppointmentsWaitListIO['input'],
    options?: LimitedSchemaFetchInfiniteQueryOptions<GetAppointmentsWaitListIO, E, D, QueryKey>,
    httpOptions?: Options
  ) => {
    const response = await queryClient.fetchInfiniteQuery(
      getGetAppointmentsWaitListQueryKey(req),
      () => SchemaAnalyticsGateway1Service.GetAppointmentsWaitList(req, httpOptions),
      options
    );

    return response;
  };

  return fetchGetAppointmentsWaitList;
};
